import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BellPlus,
  Building2,
  FileText,
  Eye,
  LoaderCircle,
  Pencil,
  Plus,
  Scale,
  Search,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import {
  ALL_COURTS_ID,
  matchesApproximateText,
} from "@tribunalea/shared";
import {
  ArgentineDateInput,
  EmptyState,
  PageHeader,
  useRouter,
} from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import {
  ActuacionDocumentDialog,
  PdfViewer,
} from "../components/ActuacionDocumentOverlays.jsx";
import { ApproximateMatchText as HighlightedText } from "../components/ApproximateMatchText.jsx";
import {
  buildDocumentViewerRoute,
  closeDocumentViewerRoute,
  DOCUMENT_VIEWER_HISTORY_STATE,
  readDocumentViewerRoute,
} from "../lib/documentViewerRoute.js";

const DAILY_LIST_STATE_KEY = "praxis:daily-list:state";

function loadSavedSearch() {
  try {
    const saved = JSON.parse(
      window.sessionStorage.getItem(DAILY_LIST_STATE_KEY) || "null",
    );
    if (!saved || typeof saved !== "object") return null;
    return {
      selection: {
        circunscripcionId: String(
          saved.selection?.circunscripcionId || "",
        ),
        typeId: String(saved.selection?.typeId || ""),
        courtId: String(saved.selection?.courtId || ""),
      },
      from: String(saved.from || ""),
      to: String(saved.to || ""),
      items: Array.isArray(saved.items) ? saved.items : [],
      resultFilter: String(saved.resultFilter || ""),
      selectedSavedFilterIds: Array.isArray(saved.selectedSavedFilterIds)
        ? saved.selectedSavedFilterIds.map(String)
        : [],
      selectedItemIds: Array.isArray(saved.selectedItemIds)
        ? saved.selectedItemIds.map(String)
        : [],
      hasSearched: Boolean(saved.hasSearched),
    };
  } catch {
    return null;
  }
}

function todayInMendoza() {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Argentina/Mendoza",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(new Date())
      .filter(({ type }) => type !== "literal")
      .map(({ type, value }) => [type, value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function titleCase(value) {
  return String(value || "")
    .toLocaleLowerCase("es")
    .replace(/(^|\s)\S/g, (letter) => letter.toLocaleUpperCase("es"));
}

function dailyListItemId(item) {
  return [
    item.cuij,
    item.actuacionId,
    item.numero,
    item.titulo,
    item.caratula,
  ]
    .filter(Boolean)
    .join(":");
}

export function DailyListsPage() {
  const api = useApi();
  const { path, search: routeSearch, navigate } = useRouter();
  const routedDocument = readDocumentViewerRoute(routeSearch);
  const today = useMemo(todayInMendoza, []);
  const savedSearch = useMemo(loadSavedSearch, []);
  const [catalog, setCatalog] = useState([]);
  const [selection, setSelection] = useState(
    savedSearch?.selection || {
      circunscripcionId: "",
      typeId: "",
      courtId: "",
    },
  );
  const [from, setFrom] = useState(savedSearch?.from || today);
  const [to, setTo] = useState(savedSearch?.to || today);
  const [items, setItems] = useState(savedSearch?.items || []);
  const [resultFilter, setResultFilter] = useState(
    savedSearch?.resultFilter || "",
  );
  const [debouncedResultFilter, setDebouncedResultFilter] = useState(
    savedSearch?.resultFilter || "",
  );
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedSavedFilterIds, setSelectedSavedFilterIds] = useState(
    savedSearch?.selectedSavedFilterIds || [],
  );
  const [selectedItemIds, setSelectedItemIds] = useState(
    savedSearch?.selectedItemIds || [],
  );
  const [creatingFollowUps, setCreatingFollowUps] = useState(false);
  const [creatingFollowUpFor, setCreatingFollowUpFor] = useState("");
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(true);
  const [filterEditorOpen, setFilterEditorOpen] = useState(false);
  const [editingFilterId, setEditingFilterId] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterPatterns, setFilterPatterns] = useState("");
  const [savingFilter, setSavingFilter] = useState(false);
  const [filterEditorError, setFilterEditorError] = useState("");
  const [documentLoading, setDocumentLoading] = useState("");
  const [attachmentLoading, setAttachmentLoading] = useState("");
  const [documentDialog, setDocumentDialog] = useState(null);
  const [pdfViewer, setPdfViewer] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(
    savedSearch?.hasSearched || false,
  );
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api
      .judiciaryCatalog()
      .then((result) => {
        if (active) setCatalog(result);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setCatalogLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    api
      .dailyListFilters()
      .then((result) => {
        if (active) setSavedFilters(result);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setSavedFiltersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(
    () => () => {
      if (pdfViewer?.url) URL.revokeObjectURL(pdfViewer.url);
    },
    [pdfViewer?.url],
  );

  useEffect(() => {
    if (!routedDocument.actId || !routedDocument.cuij) {
      setPdfViewer(null);
      setDocumentDialog((current) => current?.routed ? null : current);
      setDocumentLoading("");
      setAttachmentLoading("");
      return undefined;
    }

    let active = true;
    setDocumentDialog(null);
    setPdfViewer(null);
    if (routedDocument.attachmentId) {
      setAttachmentLoading(routedDocument.attachmentId);
      setDocumentLoading("");
    } else {
      setDocumentLoading(routedDocument.actId);
      setAttachmentLoading("");
    }

    api.actuacionDocument({
      cuij: routedDocument.cuij,
      actId: routedDocument.actId,
      attachmentId: routedDocument.attachmentId || undefined,
    }).then((result) => {
      if (!active) return;
      if (result?.status === "pdf" && result.blob?.size) {
        setPdfViewer({
          url: URL.createObjectURL(result.blob),
          fileName: result.fileName || "actuacion.pdf",
        });
        return;
      }
      if (result?.status === "select_attachment") {
        setDocumentDialog({
          routed: true,
          kind: "attachments",
          cuij: routedDocument.cuij,
          actId: routedDocument.actId,
          attachments: result.attachments || [],
        });
        return;
      }
      setDocumentDialog({
        routed: true,
        kind: "message",
        title: "PDF no disponible",
        message:
          "El Poder Judicial no informó un PDF ni archivos adjuntos para esta actuación.",
      });
    }).catch((requestError) => {
      if (!active) return;
      setDocumentDialog({
        routed: true,
        kind: "message",
        title: routedDocument.attachmentId
          ? "No pudimos abrir el adjunto"
          : "No pudimos abrir el PDF",
        message: requestError.message,
      });
    }).finally(() => {
      if (!active) return;
      setDocumentLoading("");
      setAttachmentLoading("");
    });

    return () => {
      active = false;
    };
  }, [
    api,
    routedDocument.actId,
    routedDocument.attachmentId,
    routedDocument.cuij,
  ]);

  const closeRoutedDocument = () =>
    closeDocumentViewerRoute({ path, search: routeSearch, navigate });

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedResultFilter(resultFilter),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [resultFilter]);

  useEffect(() => {
    if (!hasSearched) return;
    window.sessionStorage.setItem(
      DAILY_LIST_STATE_KEY,
      JSON.stringify({
        selection,
        from,
        to,
        items,
        resultFilter,
        selectedSavedFilterIds,
        selectedItemIds,
        hasSearched,
      }),
    );
  }, [
    selection,
    from,
    to,
    items,
    resultFilter,
    selectedSavedFilterIds,
    selectedItemIds,
    hasSearched,
  ]);

  const circunscripcion = catalog.find(
    ({ id }) => id === selection.circunscripcionId,
  );
  const types = (circunscripcion?.types || []).filter(
    ({ id }) => id !== ALL_COURTS_ID,
  );
  const type = types.find(({ id }) => id === selection.typeId);
  const courts = type?.courts || [];
  const selectedSavedFilters = useMemo(
    () =>
      savedFilters.filter(({ id }) =>
        selectedSavedFilterIds.includes(id),
      ),
    [savedFilters, selectedSavedFilterIds],
  );
  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        const itemValues = [
          item.cuij,
          item.numero,
          item.caratula,
          item.titulo,
        ];
        const matchesText = matchesApproximateText(
          itemValues,
          debouncedResultFilter,
        );
        const matchesSavedFilter =
          !selectedSavedFilters.length ||
          selectedSavedFilters.some(({ patterns }) =>
            matchesApproximateText(itemValues, patterns),
          );
        return matchesText && matchesSavedFilter;
      }),
    [debouncedResultFilter, items, selectedSavedFilters],
  );
  const hasActiveResultFilters =
    Boolean(debouncedResultFilter.trim()) ||
    selectedSavedFilters.length > 0;
  const activeHighlightPatterns = useMemo(
    () => [
      ...(debouncedResultFilter.trim()
        ? [debouncedResultFilter.trim()]
        : []),
      ...selectedSavedFilters.flatMap(({ patterns }) => patterns),
    ],
    [debouncedResultFilter, selectedSavedFilters],
  );
  const selectedItemIdSet = useMemo(
    () => new Set(selectedItemIds),
    [selectedItemIds],
  );
  const visibleItemIds = useMemo(
    () => filteredItems.map(dailyListItemId),
    [filteredItems],
  );
  const visibleSelectedCount = useMemo(
    () => visibleItemIds.filter((id) => selectedItemIdSet.has(id)).length,
    [selectedItemIdSet, visibleItemIds],
  );
  const hiddenSelectedCount =
    selectedItemIds.length - visibleSelectedCount;
  const allVisibleSelected =
    visibleItemIds.length > 0 &&
    visibleSelectedCount === visibleItemIds.length;
  const canSubmit =
    selection.circunscripcionId &&
    selection.typeId &&
    selection.courtId &&
    from &&
    to &&
    !loading;

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    setItems([]);
    setSelectedItemIds([]);
    setHasSearched(true);
    try {
      const result = await api.dailyJudiciaryList({
        ...selection,
        from,
        to,
      });
      setItems(result.items || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const openItem = (item) => {
    const target = `/expedientes/${encodeURIComponent(item.cuij)}/actuaciones`;
    const query = item.actuacionId
      ? `?actuacion=${encodeURIComponent(item.actuacionId)}`
      : "";
    navigate(`${target}${query}`);
  };

  const openItemDocument = (item) => {
    if (!item.actuacionId || documentLoading) return;
    setDocumentDialog(null);
    navigate(
      buildDocumentViewerRoute(path, routeSearch, {
        actId: item.actuacionId,
        cuij: item.cuij,
        includeCuij: true,
      }),
      {
        state: {
          ...window.history.state,
          [DOCUMENT_VIEWER_HISTORY_STATE]: true,
        },
      },
    );
  };

  const openAttachment = (attachment) => {
    if (documentDialog?.kind !== "attachments") return;
    navigate(
      buildDocumentViewerRoute(path, routeSearch, {
        actId: documentDialog.actId,
        attachmentId: attachment.id,
        cuij: documentDialog.cuij,
        includeCuij: true,
      }),
      {
        replace: true,
        state: {
          ...window.history.state,
          [DOCUMENT_VIEWER_HISTORY_STATE]: true,
        },
      },
    );
  };

  const resetFilterEditor = () => {
    setEditingFilterId("");
    setFilterName("");
    setFilterPatterns("");
    setFilterEditorError("");
  };

  const openNewFilterEditor = () => {
    resetFilterEditor();
    setFilterEditorOpen(true);
  };

  const openEditFilter = (filter) => {
    setEditingFilterId(filter.id);
    setFilterName(filter.name);
    setFilterPatterns(filter.patterns.join("\n"));
    setFilterEditorError("");
    setFilterEditorOpen(true);
  };

  const saveFilter = async (event) => {
    event.preventDefault();
    const patterns = [
      ...new Set(
        filterPatterns
          .split("\n")
          .map((pattern) => pattern.trim())
          .filter(Boolean),
      ),
    ];
    if (!filterName.trim() || !patterns.length) {
      setFilterEditorError(
        "Ingresá un nombre y al menos una expresión.",
      );
      return;
    }
    setSavingFilter(true);
    setFilterEditorError("");
    try {
      const saved = editingFilterId
        ? await api.updateDailyListFilter(editingFilterId, {
            name: filterName.trim(),
            patterns,
          })
        : await api.createDailyListFilter({
            name: filterName.trim(),
            patterns,
          });
      setSavedFilters((current) =>
        [...current.filter(({ id }) => id !== saved.id), saved].sort(
          (left, right) => left.name.localeCompare(right.name, "es"),
        ),
      );
      setSelectedSavedFilterIds((current) =>
        current.includes(saved.id) ? current : [...current, saved.id],
      );
      setFilterEditorOpen(false);
      resetFilterEditor();
    } catch (requestError) {
      setFilterEditorError(requestError.message);
    } finally {
      setSavingFilter(false);
    }
  };

  const deleteFilter = async (filter) => {
    if (!filter) return;
    if (!window.confirm(`¿Eliminar el filtro “${filter.name}”?`)) return;
    try {
      await api.deleteDailyListFilter(filter.id);
      setSavedFilters((current) =>
        current.filter(({ id }) => id !== filter.id),
      );
      setSelectedSavedFilterIds((current) =>
        current.filter((id) => id !== filter.id),
      );
      if (editingFilterId === filter.id) {
        setFilterEditorOpen(false);
        resetFilterEditor();
      }
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const toggleSavedFilter = (id) => {
    setSelectedSavedFilterIds((current) =>
      current.includes(id)
        ? current.filter((currentId) => currentId !== id)
        : [...current, id],
    );
  };

  const toggleItemSelection = (id) => {
    setSelectedItemIds((current) =>
      current.includes(id)
        ? current.filter((currentId) => currentId !== id)
        : [...current, id],
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedItemIds((current) => {
      const visible = new Set(visibleItemIds);
      if (allVisibleSelected) {
        return current.filter((id) => !visible.has(id));
      }
      return [...new Set([...current, ...visibleItemIds])];
    });
  };

  const createFollowUps = async (selectedItems, itemId = "") => {
    const cuijs = [
      ...new Set(selectedItems.map(({ cuij }) => cuij).filter(Boolean)),
    ];
    if (!cuijs.length) return;
    if (itemId) setCreatingFollowUpFor(itemId);
    else setCreatingFollowUps(true);
    setError("");
    try {
      await api.addFollowUps(cuijs);
      setSelectedItemIds([]);
      navigate("/seguimientos");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCreatingFollowUps(false);
      setCreatingFollowUpFor("");
    }
  };

  return (
    <div className="page daily-lists-page">
      <ActuacionDocumentDialog
        dialog={documentDialog}
        busy={attachmentLoading}
        onClose={() => {
          if (documentDialog?.routed) closeRoutedDocument();
          else setDocumentDialog(null);
        }}
        onSelectAttachment={openAttachment}
      />
      {pdfViewer && (
        <PdfViewer
          document={pdfViewer}
          onClose={closeRoutedDocument}
        />
      )}

      <PageHeader
        eyebrow="Praxis"
        title="Listas"
        description="Consultá los proveídos publicados por organismo y abrí directamente la actuación en su expediente."
      />

      <form className="daily-list-filter-card" onSubmit={submit}>
        <div className="daily-list-filter-heading">
          <span className="section-icon">
            <Search size={19} />
          </span>
          <div>
            <strong>Buscar en listas</strong>
            <p>Elegí el organismo y el período que querés consultar.</p>
          </div>
          {catalogLoading && (
            <span className="daily-list-catalog-loading">
              <LoaderCircle className="spin" size={15} />
              Cargando organismos…
            </span>
          )}
        </div>

        <div className="daily-list-filter-grid">
          <label className="field">
            <span>Circunscripción</span>
            <select
              value={selection.circunscripcionId}
              disabled={catalogLoading}
              required
              onChange={(event) =>
                setSelection({
                  circunscripcionId: event.target.value,
                  typeId: "",
                  courtId: "",
                })
              }
            >
              <option value="">Seleccionar</option>
              {catalog.map((option) => (
                <option key={option.id} value={option.id}>
                  {titleCase(option.label)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Tipo</span>
            <select
              value={selection.typeId}
              disabled={!selection.circunscripcionId || catalogLoading}
              required
              onChange={(event) =>
                setSelection((current) => ({
                  ...current,
                  typeId: event.target.value,
                  courtId: "",
                }))
              }
            >
              <option value="">Seleccionar</option>
              {types.map((option) => (
                <option key={option.id} value={option.id}>
                  {titleCase(option.label)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Juzgado</span>
            <select
              value={selection.courtId}
              disabled={!selection.typeId || catalogLoading}
              required
              onChange={(event) =>
                setSelection((current) => ({
                  ...current,
                  courtId: event.target.value,
                }))
              }
            >
              <option value="">Seleccionar</option>
              {courts.map((option) => (
                <option key={option.id} value={option.id}>
                  {titleCase(option.label)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Desde</span>
            <ArgentineDateInput
              value={from}
              max={to || undefined}
              required
              onChange={setFrom}
            />
          </label>

          <label className="field">
            <span>Hasta</span>
            <ArgentineDateInput
              value={to}
              min={from || undefined}
              required
              onChange={setTo}
            />
          </label>

          <button
            className="button primary daily-list-submit"
            type="submit"
            disabled={!canSubmit}
          >
            {loading ? (
              <LoaderCircle className="spin" size={18} />
            ) : (
              <Search size={18} />
            )}
            {loading ? "Buscando…" : "Buscar"}
          </button>
        </div>
      </form>

      {error && <div className="inline-error">{error}</div>}

      <section className="daily-list-results">
        <div className="daily-list-results-heading">
          <div>
            <h2>Proveídos</h2>
            {hasSearched && !loading && (
              <span>
                {hasActiveResultFilters
                  ? `${filteredItems.length} de ${items.length}`
                  : items.length}{" "}
                {items.length === 1 ? "resultado" : "resultados"}
              </span>
            )}
          </div>
        </div>

        {!loading && (
          <div className="daily-list-saved-filters">
            <div className="daily-list-saved-filter-row">
              {hasSearched && items.length > 0 && (
                <label className="daily-list-text-filter">
                  <Search size={16} />
                  <input
                    type="search"
                    value={resultFilter}
                    placeholder="Filtrar resultados"
                    aria-label="Filtrar resultados por texto"
                    onChange={(event) =>
                      setResultFilter(event.target.value)
                    }
                  />
                </label>
              )}
              <span className="daily-list-saved-filter-label">
                <SlidersHorizontal size={15} />
                Filtros guardados
              </span>
              {savedFiltersLoading ? (
                <span className="daily-list-filter-loading">
                  <LoaderCircle className="spin" size={14} />
                  Cargando…
                </span>
              ) : (
                <>
                  {savedFilters.map((filter) => (
                    <span className="daily-list-filter-chip-wrap" key={filter.id}>
                      <button
                        className={`daily-list-filter-chip ${
                          selectedSavedFilterIds.includes(filter.id)
                            ? "is-active"
                            : ""
                        }`}
                        type="button"
                        title={filter.patterns.join(" · ")}
                        onClick={() => toggleSavedFilter(filter.id)}
                      >
                        {filter.name}
                      </button>
                      <button
                        className="daily-list-filter-edit"
                        type="button"
                        aria-label={`Editar ${filter.name}`}
                        onClick={() => openEditFilter(filter)}
                      >
                        <Pencil size={12} />
                      </button>
                    </span>
                  ))}
                  <button
                    className="daily-list-add-filter"
                    type="button"
                    onClick={openNewFilterEditor}
                  >
                    <Plus size={14} />
                    Crear filtro
                  </button>
                </>
              )}
            </div>

            {filterEditorOpen && (
              <form className="daily-list-filter-editor" onSubmit={saveFilter}>
                <div className="daily-list-filter-editor-heading">
                  <strong>
                    {editingFilterId ? "Editar filtro" : "Nuevo filtro"}
                  </strong>
                  <button
                    type="button"
                    aria-label="Cerrar editor"
                    onClick={() => {
                      setFilterEditorOpen(false);
                      resetFilterEditor();
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
                <label className="field">
                  <span>Nombre</span>
                  <input
                    value={filterName}
                    maxLength={80}
                    placeholder="Ej. Recepción de escritos"
                    onChange={(event) => setFilterName(event.target.value)}
                  />
                </label>
                <label className="field">
                  <span>Expresiones — una por línea</span>
                  <textarea
                    value={filterPatterns}
                    rows={4}
                    placeholder={"por recibido\nagréguese\npase a despacho"}
                    onChange={(event) => setFilterPatterns(event.target.value)}
                  />
                </label>
                <p>
                  También encuentra abreviaturas y pequeñas variaciones, como
                  “p/recibido” o “por recib.”.
                </p>
                {filterEditorError && (
                  <div className="inline-error">{filterEditorError}</div>
                )}
                <div className="daily-list-filter-editor-actions">
                  {editingFilterId && (
                    <button
                      className="button danger subtle"
                      type="button"
                      onClick={() =>
                        deleteFilter(
                          savedFilters.find(
                            ({ id }) => id === editingFilterId,
                          ),
                        )
                      }
                    >
                      <Trash2 size={15} />
                      Eliminar
                    </button>
                  )}
                  <button
                    className="button primary"
                    type="submit"
                    disabled={savingFilter}
                  >
                    {savingFilter && (
                      <LoaderCircle className="spin" size={15} />
                    )}
                    Guardar filtro
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {!loading && hasSearched && items.length > 0 && (
          <div className="daily-list-selection-bar" aria-live="polite">
            <button
              className="daily-list-select-visible"
              type="button"
              disabled={!visibleItemIds.length}
              onClick={toggleVisibleSelection}
            >
              <span
                className={`daily-list-selection-box ${
                  allVisibleSelected ? "is-selected" : ""
                }`}
                aria-hidden="true"
              />
              {allVisibleSelected
                ? "Quitar selección visible"
                : "Seleccionar visibles"}
            </button>
            <div className="daily-list-selection-summary">
              <strong>
                {selectedItemIds.length}{" "}
                {selectedItemIds.length === 1
                  ? "seleccionado"
                  : "seleccionados"}
              </strong>
              {hiddenSelectedCount > 0 && (
                <span>
                  {hiddenSelectedCount} fuera de los filtros actuales
                </span>
              )}
            </div>
            {selectedItemIds.length > 0 && (
              <>
                <button
                  className="daily-list-clear-selection"
                  type="button"
                  onClick={() => {
                    setSelectedItemIds([]);
                  }}
                >
                  Limpiar
                </button>
                <button
                  className="button primary daily-list-follow-selected"
                  type="button"
                  disabled={creatingFollowUps}
                  onClick={() =>
                    createFollowUps(
                      items.filter((item) =>
                        selectedItemIdSet.has(dailyListItemId(item)),
                      ),
                    )
                  }
                >
                  {creatingFollowUps ? (
                    <LoaderCircle className="spin" size={16} />
                  ) : (
                    <BellPlus size={16} />
                  )}
                  {creatingFollowUps
                    ? "Agregando…"
                    : "Hacer seguimiento"}
                </button>
              </>
            )}
          </div>
        )}

        {loading ? (
          <div className="loading-list">
            <span />
            <span />
            <span />
          </div>
        ) : filteredItems.length ? (
          <div className="daily-list-items">
            {filteredItems.map((item) => {
              const itemId = dailyListItemId(item);
              const selected = selectedItemIdSet.has(itemId);
              return (
              <article
                className={`daily-list-item ${selected ? "is-selected" : ""}`}
                key={itemId}
              >
                <label
                  className="daily-list-item-selector"
                  title={selected ? "Quitar de la selección" : "Seleccionar"}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    aria-label={`Seleccionar proveído ${item.cuij}`}
                    onChange={() => toggleItemSelection(itemId)}
                  />
                  <span aria-hidden="true" />
                </label>
                <span className="daily-list-item-icon">
                  <FileText size={19} />
                </span>
                <span className="daily-list-item-content">
                  <span className="daily-list-item-meta">
                    <strong>
                      <HighlightedText
                        value={item.cuij}
                        patterns={activeHighlightPatterns}
                      />
                    </strong>
                    {item.numero && (
                      <span>
                        Expediente N.º{" "}
                        <HighlightedText
                          value={item.numero}
                          patterns={activeHighlightPatterns}
                        />
                      </span>
                    )}
                    {item.poseePdf && <span>PDF disponible</span>}
                  </span>
                  <strong className="daily-list-item-title">
                    <HighlightedText
                      value={
                        item.titulo || "Proveído sin título informado"
                      }
                      patterns={activeHighlightPatterns}
                    />
                  </strong>
                  <span className="daily-list-item-caratula">
                    <Scale size={13} />
                    <HighlightedText
                      value={item.caratula || "Sin carátula informada"}
                      patterns={activeHighlightPatterns}
                    />
                  </span>
                </span>
                <span className="daily-list-item-actions">
                  <button
                    className="daily-list-item-action is-follow"
                    type="button"
                    disabled={
                      creatingFollowUps ||
                      Boolean(creatingFollowUpFor)
                    }
                    onClick={() => createFollowUps([item], itemId)}
                  >
                    {creatingFollowUpFor === itemId ? (
                      <LoaderCircle className="spin" size={16} />
                    ) : (
                      <BellPlus size={16} />
                    )}
                    {creatingFollowUpFor === itemId
                      ? "Agregando…"
                      : "Hacer seguimiento"}
                  </button>
                  <button
                    className="daily-list-item-action"
                    type="button"
                    onClick={() => openItem(item)}
                  >
                    Ver actuación
                    <ArrowRight size={17} />
                  </button>
                  <button
                    className="daily-list-item-action is-pdf"
                    type="button"
                    disabled={
                      !item.actuacionId ||
                      Boolean(documentLoading)
                    }
                    onClick={() => openItemDocument(item)}
                  >
                    {documentLoading === item.actuacionId ? (
                      <LoaderCircle className="spin" size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                    {documentLoading === item.actuacionId
                      ? "Buscando…"
                      : "Ver PDF"}
                  </button>
                </span>
              </article>
              );
            })}
          </div>
        ) : hasSearched && items.length && hasActiveResultFilters ? (
          <EmptyState
            icon={Search}
            title="No hay coincidencias"
            description="Probá con otro texto o desactivá alguno de los filtros guardados."
          />
        ) : hasSearched ? (
          <EmptyState
            icon={FileText}
            title="No encontramos proveídos"
            description="No hay publicaciones para ese organismo y período."
          />
        ) : (
          <EmptyState
            icon={Building2}
            title="Elegí un organismo"
            description="Aplicá los filtros para consultar su lista de proveídos."
          />
        )}
      </section>
    </div>
  );
}
